import { CartItem } from "@/stores/cartStore";
import { apiClient } from "../core/apiClient";

/**
 * Interfaz para los datos del pedido
 */
export interface OrderData {
  items: CartItem[];
  total: number;
  user: {
    id?: string; // Opcional para usuarios anónimos
    nombres: string;
    apellidos: string;
    email: string;
  };
}

/**
 * Interfaz para la respuesta del pedido (sistema anterior)
 */
export interface OrderResponse {
  orderId: string;
  qrCodePayload: string;
  orderDate: string;
  total: number;
  items: CartItem[];
  user: {
    id?: string; // Opcional para usuarios anónimos
    nombres: string;
    apellidos: string;
    email: string;
  };
  status: "pending" | "paid" | "completed" | "cancelled";
}

/**
 * Interfaz para las órdenes del backend
 */
export interface BackendOrder {
  id: string;
  total: number;
  estado: string;
  qrCode: string | null;
  createdAt: string;
  itemsCount: number;
  items: BackendOrderItem[];
}

/**
 * Interfaz para los items de orden del backend
 */
export interface BackendOrderItem {
  id: string;
  productName: string;
  quantity: number;
  precioUnitario: number;
  subtotal: number;
}

/**
 * Interfaz para el detalle completo de una orden
 */
export interface OrderDetail {
  id: string;
  total: number;
  estado: string;
  qrCode: string | null;
  createdAt: string;
  lastScanAt: string | null;
  totalScans: number;
  qrDeliveryEnabled: boolean;
  context?: string; // Contexto desde donde se accede al detalle
  items: OrderDetailItem[];
}

/**
 * Interfaz para los items de detalle de orden
 */
export interface OrderDetailItem {
  id: string;
  productName: string;
  quantity: number;
  initialQuantity: number;
  remainingQuantity: number;
  precioUnitario: number;
  subtotal: number;
  productType: 'single' | 'pack';
}

/**
 * Interfaz para estadísticas de pedidos
 */
export interface OrderStats {
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  ordersThisMonth: number;
}

/**
 * Interfaz para pedidos en preparación desde el backend
 */
export interface PreparationOrder {
  id: string;
  estado: string;
  total: number;
  cliente_nombre_anonimo: string;
  cliente_email_anonimo: string | null;
  creadoEn: string;
  tiempoEnPreparacion: string;
  last_scan_at: string | null;
}

/**
 * Interfaz para la respuesta de pedidos en preparación
 */
export interface PreparationOrdersResponse {
  success: boolean;
  message: string;
  data: {
    orders: PreparationOrder[];
  };
}

/**
 * Servicio para manejo de órdenes
 */
class OrderService {
  /**
   * Crea un nuevo pedido en el sistema
   * @param orderData - Datos del pedido a crear
   * @returns Promise con la respuesta del pedido
   */
  static async createOrder(orderData: OrderData): Promise<OrderResponse> {
    // Simular delay de procesamiento de pago
    await new Promise((resolve) => setTimeout(resolve, 2500));

    // Generar ID único para el pedido
    const orderId = `ORD-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)
      .toUpperCase()}`;

    // Generar payload para QR code
    const qrCodePayload = this.generateQRPayload(orderId, orderData);

    // Crear objeto de respuesta
    const orderResponse: OrderResponse = {
      orderId,
      qrCodePayload,
      orderDate: new Date().toISOString(),
      total: orderData.total,
      items: orderData.items,
      user: orderData.user,
      status: "paid",
    };

    // Simular almacenamiento en "base de datos"
    this.storeOrder(orderResponse);

    return orderResponse;
  }

  /**
   * Genera el payload para el QR code
   * @param orderId - ID del pedido
   * @param orderData - Datos del pedido
   * @returns String con el payload del QR
   */
  private static generateQRPayload(
    orderId: string,
    orderData: OrderData
  ): string {
    const qrData = {
      orderId,
      timestamp: Date.now(),
      total: orderData.total,
      itemsCount: orderData.items.length,
      user: {
        id: orderData.user.id || "anonymous",
        name: `${orderData.user.nombres} ${orderData.user.apellidos}`,
      },
      items: orderData.items.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
      })),
    };

    return JSON.stringify(qrData);
  }

  /**
   * Almacena el pedido (simulación)
   * @param order - Pedido a almacenar
   */
  private static storeOrder(order: OrderResponse): void {
    // En un entorno real, esto se guardaría en la base de datos
    // Por ahora, solo simulamos el almacenamiento
    const orders = this.getStoredOrders();
    orders.push(order);
    localStorage.setItem("chilenito-orders", JSON.stringify(orders));
  }

  /**
   * Obtiene los pedidos almacenados
   * @returns Array de pedidos
   */
  private static getStoredOrders(): OrderResponse[] {
    const stored = localStorage.getItem("chilenito-orders");
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Obtiene un pedido específico por ID
   * @param orderId - ID del pedido
   * @returns Promise con el pedido o null si no existe
   */
  static async getOrderById(orderId: string): Promise<OrderResponse | null> {
    console.log("🔍 OrderService.getOrderById - Buscando ID:", orderId);
    
    // Buscar en localStorage primero para órdenes anónimas
    const storedOrders = this.getStoredOrders();
    const localOrder = storedOrders.find((o) => o.orderId === orderId);
    console.log("📦 Orden en localStorage:", localOrder ? "ENCONTRADA" : "NO ENCONTRADA");
    
    if (localOrder) {
      return localOrder;
    }
    
    try {
      // Intentar endpoint directo de órdenes
      const response = await apiClient.get(`/api/v1/orders/${orderId}`);
      console.log("📥 Respuesta del backend:", response.data);
      
      if (response.data.success && response.data.data) {
        const backendOrder = response.data.data;
        console.log("✅ Orden encontrada en backend:", backendOrder);
        
        const mappedOrder: OrderResponse = {
          orderId: backendOrder.id,
          qrCodePayload: "",
          orderDate: backendOrder.createdAt || backendOrder.creadoEn,
          total: Number(backendOrder.total),
          items: backendOrder.items || [],
          user: {
            id: "anonymous",
            nombres: backendOrder.cliente_nombre_anonimo || "Cliente",
            apellidos: "Anónimo",
            email: backendOrder.cliente_email_anonimo || ""
          },
          status: this.mapBackendStatus(backendOrder.estado)
        };
        
        return mappedOrder;
      }
    } catch (backendError: any) {
      console.log("⚠️ Error consultando backend:", backendError.response?.status, backendError.response?.data);
    }

    return null;
  }

  /**
   * Mapea el estado del backend al formato esperado
   */
  private static mapBackendStatus(estado: string): "pending" | "paid" | "completed" | "cancelled" {
    const statusMap: Record<string, "pending" | "paid" | "completed" | "cancelled"> = {
      'PENDIENTE': 'pending',
      'PAGADO': 'paid',
      'EN_PREPARACION': 'paid', // Las órdenes en preparación ya están pagadas
      'PREPARADO': 'completed',
      'ENTREGADO': 'completed',
      'CANCELADO': 'cancelled'
    };
    
    return statusMap[estado?.toUpperCase()] || 'pending';
  }

  /**
   * Obtiene estadísticas de pedidos
   * @returns Promise con estadísticas
   */
  static async getOrderStats(): Promise<OrderStats> {
    // Simular delay de red
    await new Promise((resolve) => setTimeout(resolve, 300));

    const orders = this.getStoredOrders();
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => sum + order.total, 0);
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Calcular pedidos del mes actual
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const ordersThisMonth = orders.filter((order) => {
      const orderDate = new Date(order.orderDate);
      return (
        orderDate.getMonth() === currentMonth &&
        orderDate.getFullYear() === currentYear
      );
    }).length;

    return {
      totalOrders,
      totalRevenue,
      averageOrderValue,
      ordersThisMonth,
    };
  }

  /**
   * Obtiene el historial de pedidos de un usuario (sistema anterior)
   * @param userId - ID del usuario
   * @returns Promise con array de pedidos
   */
  static async getUserOrders(userId: string): Promise<OrderResponse[]> {
    // Simular delay de red
    await new Promise((resolve) => setTimeout(resolve, 500));

    const orders = this.getStoredOrders();
    return orders.filter((order) => order.user.id === userId);
  }

  /**
   * Obtiene el historial de órdenes desde el backend
   * @returns Promise con array de órdenes del backend
   */
  static async getBackendOrders(): Promise<BackendOrder[]> {
    try {
      const response = await apiClient.get('/api/v1/orders');
      
      if (response.data.success) {
        return response.data.data.orders;
      } else {
        throw new Error(response.data.message || 'Error al obtener órdenes');
      }
    } catch (error: any) {
      console.error('Error obteniendo órdenes del backend:', error);
      throw new Error(error.response?.data?.message || 'Error al obtener órdenes');
    }
  }

  /**
   * Obtiene el historial de compras del usuario autenticado usando transacciones
   * Mapea transacciones -> órdenes únicas y obtiene el conteo real de productos
   */
  static async getMyOrdersFromTransactions(): Promise<BackendOrder[]> {
    try {
      const response = await apiClient.get('/api/v1/transactions/mine');
      const data = response.data;
      if (!data?.success) {
        throw new Error(data?.message || 'Error al obtener transacciones del usuario');
      }

      const transactions = (data.data?.transactions || []) as any[];
      const ordersMap = new Map<string, BackendOrder>();

      // Primero, recolectamos todas las órdenes únicas de las transacciones
      for (const tx of transactions) {
        const o = tx.order;
        if (!o || !o.id) continue;
        if (!ordersMap.has(o.id)) {
          ordersMap.set(o.id, {
            id: o.id,
            total: Number(o.total ?? tx.monto ?? 0),
            estado: o.estado ?? 'PAGADO',
            qrCode: o.qr_code ?? null,
            createdAt: o.creadoEn ?? tx.creadaEn,
            itemsCount: 0, // Inicializamos en 0, lo actualizaremos después
            items: [],
          });
        }
      }

      // Ahora obtenemos el conteo real de productos para cada orden
      const ordersWithItems = await Promise.all(
        Array.from(ordersMap.values()).map(async (order) => {
          try {
            // Intentamos obtener el conteo de productos directamente
            const itemsCount = await this.getOrderItemsCount(order.id);
            return {
              ...order,
              itemsCount: itemsCount,
              items: [], // No necesitamos los items completos para el listado
            };
          } catch (error) {
            console.warn(`No se pudo obtener conteo de productos para orden ${order.id}:`, error);
            // Si no podemos obtener el conteo, mantenemos el conteo en 0
            return order;
          }
        })
      );

      return ordersWithItems;
    } catch (error: any) {
      console.error('Error obteniendo órdenes desde transacciones:', error);
      throw new Error(error.response?.data?.message || 'Error al obtener compras del usuario');
    }
  }

  /**
   * Obtiene el conteo de productos de una orden específica
   * @param orderId - ID de la orden
   * @returns Promise con el número de productos
   */
  static async getOrderItemsCount(orderId: string): Promise<number> {
    try {
      const response = await apiClient.get(`/api/v1/orders/${orderId}/items-count`);
      
      if (response.data.success) {
        return response.data.data.count || 0;
      } else {
        throw new Error(response.data.message || 'Error al obtener conteo de productos');
      }
    } catch (error: any) {
      console.error('Error obteniendo conteo de productos:', error);
      // Si el endpoint no existe, intentamos con el detalle completo como fallback
      try {
        const orderDetail = await this.getOrderDetail(orderId);
        return orderDetail.items?.length || 0;
      } catch (fallbackError) {
        console.error('Error en fallback para obtener conteo:', fallbackError);
        return 0;
      }
    }
  }

  /**
   * Obtiene el detalle de una orden específica desde el backend
   * @param orderId - ID de la orden
   * @returns Promise con el detalle de la orden
   */
  static async getOrderDetail(orderId: string): Promise<OrderDetail> {
    try {
      const response = await apiClient.get(`/api/v1/orders/${orderId}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al obtener detalle de orden');
      }
    } catch (error: any) {
      console.error('Error obteniendo detalle de orden:', error);
      throw new Error(error.response?.data?.message || 'Error al obtener detalle de orden');
    }
  }

  /**
   * Obtiene los pedidos en preparación para el KDS
   * @returns Promise con array de pedidos en preparación
   */
  static async getPreparationOrders(): Promise<PreparationOrder[]> {
    console.log('🔄 OrderService.getPreparationOrders');
    try {
      const url = '/api/v1/orders/today/in-preparation';
      console.log('📤 Enviando GET a:', url);
      
      const response = await apiClient.get(url);
      
      console.log('📥 Respuesta de preparación:', response.data);
      
      if (response.data.success) {
        return response.data.data.orders;
      } else {
        throw new Error(response.data.message || 'Error al obtener pedidos en preparación');
      }
    } catch (error: any) {
      console.error('❌ Error obteniendo pedidos en preparación:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      throw new Error(error.response?.data?.message || 'Error al obtener pedidos en preparación');
    }
  }

  /**
   * Obtiene todos los pedidos activos del día (preparación + listos)
   * @returns Promise con array de todos los pedidos activos
   */
  static async getTodayActiveOrders(): Promise<PreparationOrder[]> {
    console.log('🔄 OrderService.getTodayActiveOrders');
    try {
      // Intentar endpoint unificado primero
      const unifiedUrl = '/api/v1/orders/today/active';
      console.log('📤 Intentando endpoint unificado:', unifiedUrl);
      
      try {
        const response = await apiClient.get(unifiedUrl);
        if (response.data.success) {
          console.log('✅ Endpoint unificado encontrado');
          return response.data.data.orders;
        }
      } catch (unifiedError: any) {
        console.log('❌ Endpoint unificado no disponible:', unifiedError.response?.status);
      }

      // Fallback: hacer llamadas separadas
      console.log('🔄 Usando llamadas separadas...');
      const [preparationOrders, readyOrders] = await Promise.all([
        this.getPreparationOrders(),
        this.getReadyOrders()
      ]);

      return [...preparationOrders, ...readyOrders];
    } catch (error: any) {
      console.error('❌ Error obteniendo pedidos activos:', error);
      throw new Error('Error al obtener pedidos activos del día');
    }
  }

  /**
   * Obtiene los pedidos listos para retirar
   * @returns Promise con array de pedidos listos
   */
  static async getReadyOrders(): Promise<PreparationOrder[]> {
    console.log('🔄 OrderService.getReadyOrders');
    try {
      // Intentar diferentes endpoints posibles para pedidos listos
      const possibleUrls = [
        '/api/v1/orders/today/ready',
        '/api/v1/orders/today/prepared',
        '/api/v1/orders/ready',
        '/api/v1/orders/prepared'
      ];

      for (const url of possibleUrls) {
        try {
          console.log('📤 Probando endpoint:', url);
          const response = await apiClient.get(url);
          
          if (response.data.success) {
            console.log('✅ Endpoint encontrado:', url);
            return response.data.data.orders;
          }
        } catch (error: any) {
          console.log('❌ Endpoint no disponible:', url, error.response?.status);
          continue;
        }
      }

      // Si no hay endpoint específico, devolver array vacío
      console.log('⚠️ No se encontró endpoint para pedidos listos, devolviendo array vacío');
      return [];
    } catch (error: any) {
      console.error('❌ Error obteniendo pedidos listos:', error);
      return []; // Devolver array vacío en lugar de fallar
    }
  }

  /**
   * Marca un pedido como listo para entrega
   * @param orderId - ID del pedido
   * @returns Promise con boolean indicando el éxito
   */
  static async markOrderAsReady(orderId: string): Promise<boolean> {
    console.log('🔄 OrderService.markOrderAsReady - ID:', orderId);
    try {
      const url = `/api/v1/orders/${orderId}/status`;
      const payload = { estado: 'PREPARADO' }; // Estado correcto según el backend
      
      console.log('📤 Enviando PATCH a:', url);
      console.log('📤 Payload:', payload);
      
      const response = await apiClient.patch(url, payload);
      
      console.log('📥 Respuesta completa:', response.data);
      console.log('📥 Status code:', response.status);
      
      return response.data.success;
    } catch (error: any) {
      console.error('❌ Error en markOrderAsReady:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      throw new Error(error.response?.data?.message || 'Error al marcar pedido como listo');
    }
  }

  /**
   * Actualiza el estado de un pedido
   * @param orderId - ID del pedido
   * @param newStatus - Nuevo estado del pedido
   * @returns Promise con boolean indicando el éxito
   */
  static async updateOrderStatus(orderId: string, newStatus: string): Promise<boolean> {
    console.log('🔄 OrderService.updateOrderStatus - ID:', orderId, 'Estado:', newStatus);
    try {
      const url = `/api/v1/orders/${orderId}/status`;
      const payload = { estado: newStatus };
      
      console.log('📤 Enviando PATCH a:', url);
      console.log('📤 Payload:', payload);
      
      const response = await apiClient.patch(url, payload);
      
      console.log('📥 Respuesta completa:', response.data);
      console.log('📥 Status code:', response.status);
      
      return response.data.success;
    } catch (error: any) {
      console.error('❌ Error en updateOrderStatus:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      throw new Error(error.response?.data?.message || 'Error al actualizar estado del pedido');
    }
  }

  /**
   * Marca un pedido como entregado
   * @param orderId - ID del pedido
   * @returns Promise con boolean indicando el éxito
   */
  static async markOrderAsDelivered(orderId: string): Promise<boolean> {
    return this.updateOrderStatus(orderId, 'ENTREGADO');
  }
}

export default OrderService;
